import React, { useEffect, useState, useRef } from 'react';
import { Card, Spinner } from 'react-bootstrap';
import useAuth from '../../pages/useAuth';
import EmployeeHeader from '../../components/EmployeeHeader';

const EmployeesProfile = ({ data }) => { // <-- Accept data prop
     useAuth();
     const baseUrl = process.env.REACT_APP_BASE_URL;
     const [attendanceData, setAttendanceData] = useState(null);
     const [loading, setLoading] = useState(true);
     const [statusCounts, setStatusCounts] = useState(null);
     const [employeeDetails, setEmployeeDetails] = useState(null);

     const [currentEmployee, setCurrentEmployee] = useState({
          photo: 'https://randomuser.me/api/portraits/men/32.jpg',
          name: '',
          id: '',
          role: ''
     });

     const [showPopup, setShowPopup] = useState(false);
     const popupRef = useRef(null);
     const buttonRef = useRef(null);

     // 🧍‍♂️ Load current user info from session
     useEffect(() => {
          const userCode = sessionStorage.getItem('user_code');
          const userName = sessionStorage.getItem('user_name');
          const userRole = sessionStorage.getItem('user_role');
          setCurrentEmployee((prev) => ({
               ...prev,
               name: userName,
               id: userCode,
               role: userRole || 'User'
          }));
     }, []);

     // 📌 Use data prop instead of fetching
     useEffect(() => {
          if (data) {
               setEmployeeDetails({
                    emp_code: data.employee_code,
                    full_name: data.employee_name,
                    gender: data.gender,
                    dob: data.dob,
                    mobile: data.mobile,
                    emergency_contact: data.emergency_contact,
                    mail_id: data.email,
                    comp_name: data.company,
                    dept_name: data.department,
                    designation_name: data.designation,
                    ou_name: data.cluster,
                    location_name: data.location_name,
                    city: data.city_name,
                    region: data.cluster,
                    country: 'India',
                    report_mngr: data.reporting_manager_name,
                    function_mngr: data.functional_manager_name,
                    doj: data.joining_date,
                    emp_status: data.status,
                    active: data.Active_Inactive,
                    validity: data.latest_agreement_valid_date,
                    grade: data.grade_name,
                    position: data.position,
                    cost_center: '',
                    pay_group: data.pay_group,
                    bank_name: data.bank_account_name,
                    bank_acnum: data.bank_account_number,
                    ifsc_code: data.ifsc_code,
                    exit_date: data.relieving_date,
                    created_on: data.created_at,
                    updated_at: data.updated_at,
               });
          }
     }, [data]);

     // 📝 Fetch Attendance Data
     useEffect(() => {
          const fetchAttendance = async () => {
               try {
                    setLoading(true);
                    const token = sessionStorage.getItem('token');
                    const response = await fetch(`${baseUrl}/getUserAttendance`, {
                         method: 'GET',
                         headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                         },
                    });
                    const data = await response.json();
                    if (data.status) {
                         setAttendanceData(data.data);
                    }
               } catch (error) {
                    console.error('Error fetching attendance:', error);
               } finally {
                    setLoading(false);
               }
          };

          fetchAttendance();
     }, [baseUrl]);

     // 📊 Fetch Status Counts
     useEffect(() => {
          const fetchStatusCounts = async () => {
               try {
                    const token = sessionStorage.getItem('token');
                    const response = await fetch(`${baseUrl}/getStatus`, {
                         method: 'GET',
                         headers: {
                              'Content-Type': 'application/json',
                              Authorization: `Bearer ${token}`,
                         },
                    });

                    const result = await response.json();
                    if (result.status) {
                         setStatusCounts(result.data);
                    }
               } catch (error) {
                    console.error('Error fetching status counts:', error);
               }
          };

          fetchStatusCounts();
     }, [baseUrl]);

     // 📌 Close popup on outside click
     useEffect(() => {
          const handleClickOutside = (event) => {
               if (
                    popupRef.current &&
                    !popupRef.current.contains(event.target) &&
                    buttonRef.current &&
                    !buttonRef.current.contains(event.target)
               ) {
                    setShowPopup(false);
               }
          };

          document.addEventListener('mousedown', handleClickOutside);
          return () => {
               document.removeEventListener('mousedown', handleClickOutside);
          };
     }, []);

     // Helper function to format full name
     const getFullName = (details) => {
          if (!details) return '-';
          return details.full_name || '-';
     };

     // Helper function to format date
     const formatDate = (dateString) => {
          if (!dateString) return '-';
          const date = new Date(dateString);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
     };

     // Helper function to get gender display
     const getGenderDisplay = (gender) => {
          if (!gender) return '-';
          return gender;
     };

     // Helper function to get status display
     const getStatusDisplay = (empStatus) => {
          if (!empStatus) return '-';
          return empStatus === 'A' ? 'Active' : empStatus === 'I' ? 'Inactive' : empStatus;
     };

     return (
          <>


               <div style={{ padding: '24px', backgroundColor: '#f7f9fc', minHeight: '100vh' }}>
                    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
                         <Card style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
                              <Card.Body>


                                   {!employeeDetails ? (
                                        <div style={{ textAlign: 'center', padding: '50px' }}>
                                             <Spinner animation="border" variant="primary" />
                                             <p style={{ marginTop: '16px', color: '#666' }}>Loading employee details...</p>
                                        </div>
                                   ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                                             {/* ...existing InfoRow components... */}
                                             <InfoRow label="Employee Code" value={employeeDetails.emp_code} icon="👤" />
                                             <InfoRow label="Full Name" value={getFullName(employeeDetails)} icon="📛" />
                                             <InfoRow label="Gender" value={getGenderDisplay(employeeDetails.gender)} icon="⚥" />
                                             <InfoRow label="Date of Birth" value={formatDate(employeeDetails.dob)} icon="🎂" />
                                             <InfoRow label="Mobile" value={employeeDetails.mobile} icon="📱" />
                                             <InfoRow label="Emergency Contact" value={employeeDetails.emergency_contact} icon="🚨" />
                                             <InfoRow label="Email" value={employeeDetails.mail_id} icon="📧" />
                                             <InfoRow label="Company" value={employeeDetails.comp_name} icon="🏢" />
                                             <InfoRow label="Department" value={employeeDetails.dept_name} icon="🏛️" />
                                             <InfoRow label="Designation" value={employeeDetails.designation_name} icon="💼" />
                                             <InfoRow label="Organization Unit" value={employeeDetails.ou_name} icon="🏗️" />
                                             <InfoRow label="Location" value={employeeDetails.location_name} icon="📍" />
                                             <InfoRow label="City" value={employeeDetails.city} icon="🌆" />
                                             <InfoRow label="Region" value={employeeDetails.region} icon="🗺️" />
                                             <InfoRow label="Country" value={employeeDetails.country} icon="🌍" />
                                             <InfoRow label="Reporting Manager" value={employeeDetails.report_mngr} icon="👔" />
                                             <InfoRow label="Functional Manager" value={employeeDetails.function_mngr} icon="👨‍💼" />
                                             <InfoRow label="Date of Joining" value={formatDate(employeeDetails.doj)} icon="📅" />

                                             <InfoRow label="Grade" value={employeeDetails.grade} icon="🎖️" />
                                             <InfoRow label="Position" value={employeeDetails.position} icon="🎯" />
                                             <InfoRow label="Cost Center" value={employeeDetails.cost_center} icon="💰" />
                                             <InfoRow label="Pay Group" value={employeeDetails.pay_group} icon="💳" />
                                             <InfoRow label="Bank Name" value={employeeDetails.bank_name} icon="🏦" />
                                             <InfoRow label="Account Number" value={employeeDetails.bank_acnum} icon="🔢" />
                                             <InfoRow label="IFSC Code" value={employeeDetails.ifsc_code} icon="🏧" />

                                        </div>
                                   )}
                              </Card.Body>
                         </Card>
                    </div>
               </div>
          </>
     );
};

// InfoRow component
const InfoRow = ({ label, value, icon }) => (
     <div style={{
          padding: '12px 16px',
          backgroundColor: '#fff',
          border: '1px solid #e1e8ed',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px'
     }}>
          <div style={{
               fontSize: '12px',
               color: '#8899a6',
               fontWeight: '500',
               textTransform: 'uppercase',
               letterSpacing: '0.5px',
               display: 'flex',
               alignItems: 'center',
               gap: '6px'
          }}>
               <span>{icon}</span>
               <span>{label}</span>
          </div>
          <div style={{
               fontSize: '14px',
               color: '#2c3e50',
               fontWeight: '500'
          }}>
               {value || '-'}
          </div>
     </div>
);


export default EmployeesProfile;